import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { AbstractService } from '../commons/abstract.service';
import { Customer } from "../models/customer.model";
import { CustomerContactRoleEnum } from "../models/enum/customer-contact-role.enum";

/** Body of `POST /api/customer/:id/contacts` (E2 RF-01): a person picked from the base (`contactId`) or typed (name + phone). */
export interface AddCustomerContactRequest {
  contactId?: string;
  name?: string;
  phone?: string;
  email?: string;
  contactPosition?: string;
  role?: CustomerContactRoleEnum;
}

@Injectable({ providedIn: 'root' })
export class CustomerService extends AbstractService<Customer> {
  constructor(private http: HttpClient) {
    super(http);
  }

  getURLBase(): string {
    return '/customer';
  }

  preCreate(obj: Customer): void {
    delete obj._id;
  }

  preUpdate(obj: Customer): void {
  }

  /** Adds a person to the account with a role; answers the account populated as the detail reads it (E2 RF-01). */
  addContact(idCustomer: string, body: AddCustomerContactRequest): Observable<Customer> {
    return super.post(`/${idCustomer}/contacts`, body);
  }

  /** Removes a person from the account; the `Contact` itself is untouched. */
  removeContact(idCustomer: string, idContact: string): Observable<Customer> {
    return super.deleteWithParams(`/${idCustomer}/contacts/${idContact}`);
  }

  /** Makes an additional contact the primary — the messaging identity (E2 RF-03). */
  promoteContact(idCustomer: string, idContact: string): Observable<Customer> {
    return super.post(`/${idCustomer}/contacts/${idContact}/promote`, {});
  }

  /**
   * Blacklists a lead, or lifts it (RF-17, RF-37).
   *
   * Moved here from `ContactService` on 2026-08-21: the markings are enrichment and
   * enrichment lives on the CRM record of the lead. NOT the same as
   * `ContactService.blockContact`, which blocks the contact on a specific channel and is
   * an attendance action — the two share the `blockContact` permission and nothing else,
   * and the backend refuses without it.
   */
  setBlacklisted(idCustomer: string, value: boolean): Observable<Customer> {
    return super.post(`/${idCustomer}/blacklist/${value}`, {});
  }

  /** E3 RF-22/AC-15: how many open deals blacklisting this account's primary contact withdraws — asked before confirming. */
  getBlacklistImpact(idCustomer: string): Observable<{ deals: number }> {
    return super.get(`/${idCustomer}/blacklist/impact`);
  }

  /**
   * Marks a lead as outside the ideal customer profile, or lifts it (RF-18, RF-37).
   *
   * Supervisors and administrators only — the backend answers 403 for anyone else. The
   * deals stay visible on the board, carrying the out-of-profile marker.
   */
  setOutOfIcp(idCustomer: string, value: boolean): Observable<Customer> {
    return super.post(`/${idCustomer}/out-of-icp/${value}`, {});
  }
}
